import { z } from "zod";

/**
 * Folders for an organisation's decks, inside one another to any depth up to
 * MAX_FOLDER_DEPTH: Clients › Safaricom › AGM 2026. Shared, like the decks in them, by
 * everyone in the organisation.
 */

/** How deep folders go. Deep enough for client › event › day, shallow enough to find things. */
export const MAX_FOLDER_DEPTH = 5;

const folderNameSchema = z.string().trim().min(1).max(100);

/** One folder, with how much is directly inside it. */
export const folderSchema = z
  .object({
    folderId: z.string().uuid(),
    name: z.string(),
    /** The folder it sits in; null at the top level. */
    parentId: z.string().uuid().nullable(),
    decks: z.number().int().nonnegative(),
    folders: z.number().int().nonnegative(),
  })
  .strict();
export type Folder = z.infer<typeof folderSchema>;

export const createFolderSchema = z
  .object({ name: folderNameSchema, parentId: z.string().uuid().nullable().default(null) })
  .strict();
export type CreateFolderRequest = z.infer<typeof createFolderSchema>;

/** Rename a folder, move it into another (null: to the top level), or both. */
export const updateFolderSchema = z
  .object({ name: folderNameSchema.optional(), parentId: z.string().uuid().nullable().optional() })
  .strict()
  .refine((r) => r.name !== undefined || r.parentId !== undefined, { message: "Nothing to change" });
export type UpdateFolderRequest = z.infer<typeof updateFolderSchema>;

/** File a deck in a folder, or at the top level with null. */
export const moveDeckSchema = z.object({ folderId: z.string().uuid().nullable() }).strict();
export type MoveDeckRequest = z.infer<typeof moveDeckSchema>;

/**
 * The folders from the top down to `folderId`, itself included: the dashboard's path.
 * Empty for the top level, or for a folder that isn't in the list.
 */
export function folderPath(folders: readonly Folder[], folderId: string | null): Folder[] {
  const byId = new Map(folders.map((f) => [f.folderId, f]));
  const path: Folder[] = [];
  for (let id = folderId; id && path.length <= folders.length; ) {
    const folder = byId.get(id);
    if (!folder) return [];
    path.unshift(folder);
    id = folder.parentId;
  }
  return path;
}

/**
 * Every folder in tree order — each followed by what is inside it, by name — with its
 * depth, for a picker that shows the tree as an indented list.
 */
export function folderTree(folders: readonly Folder[]): { folder: Folder; depth: number }[] {
  const children = new Map<string | null, Folder[]>();
  for (const f of folders) {
    const siblings = children.get(f.parentId) ?? [];
    siblings.push(f);
    children.set(f.parentId, siblings);
  }
  for (const list of children.values()) list.sort((a, b) => a.name.localeCompare(b.name));
  const out: { folder: Folder; depth: number }[] = [];
  const visit = (parent: string | null, depth: number) => {
    for (const folder of children.get(parent) ?? []) {
      out.push({ folder, depth });
      visit(folder.folderId, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

/** The folder and every folder inside it, at any depth: where it can't be moved to. */
export function folderAndInside(folders: readonly Folder[], folderId: string): Set<string> {
  const inside = new Set([folderId]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const f of folders) {
      if (f.parentId && inside.has(f.parentId) && !inside.has(f.folderId)) {
        inside.add(f.folderId);
        grew = true;
      }
    }
  }
  return inside;
}

/** How many levels a folder and what is inside it take up: 1 for a folder with no folders in it. */
export function folderHeight(folders: readonly Folder[], folderId: string): number {
  const below = folders.filter((f) => f.parentId === folderId);
  return 1 + Math.max(0, ...below.map((f) => folderHeight(folders, f.folderId)));
}
